import { useCallback, useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { get } from "radash"
import { useShallow } from "zustand/react/shallow"

import type { PaymentOption, StaticConfigItem } from "@be-miniapp/api"
import { v1Configuration } from "@be-miniapp/api"
import { PaymentOptionKey } from "@be-miniapp/types"

import { useCustomerPaymentStaticConfig } from "../queries/use-customer-payment-static-config"
import {
  selectRideBooking,
  selectRideLiveSession,
  selectRidePickupDropoff,
} from "../stores/ride-data.selectors"
import type { RideDataStoreHook } from "./use-fare-page-model"

export const FIND_DRIVER_SNAP_POINTS = [0.38, 0.8] as const

export interface UseFindDriverBottomSheetModelDeps {
  useRideDataStore: RideDataStoreHook
  usePaymentMethod: (params: {
    paymentMethodCode: number | undefined
  }) => PaymentOption | undefined
  /** App `useBeUserData()` — shape differs by platform; only `data.user_name` is read for cancel. */
  useBeUserData: () => unknown
}

export function useFindDriverBottomSheetModel(
  deps: UseFindDriverBottomSheetModelDeps
) {
  const { useRideDataStore, usePaymentMethod, useBeUserData } = deps

  const { pickup, dropoff } = useRideDataStore(
    useShallow(selectRidePickupDropoff)
  )
  const { sessionId } = useRideDataStore(useShallow(selectRideLiveSession))
  const { paymentMethod: selectedPaymentMethod } = useRideDataStore(
    useShallow(selectRideBooking)
  )

  const defaultPaymentMethod = usePaymentMethod({
    paymentMethodCode: PaymentOptionKey.CASH,
  })
  const currentPaymentMethod = selectedPaymentMethod ?? defaultPaymentMethod

  const beUser = useBeUserData() as { data?: { user_name?: string } } | null

  const { data: autosConfiguration } = useQuery({
    queryKey: ["autos-configuration"],
    queryFn: async () => v1Configuration({}),
    staleTime: 60 * 60 * 1000,
  })

  const countdownValue =
    autosConfiguration?.configs?.app_request_ride_timeout || 200

  const { data: paymentStatic } = useCustomerPaymentStaticConfig()

  const currentPaymentMethodStatic = useMemo(() => {
    const paymentStatics = get(
      paymentStatic,
      "data.data",
      get(paymentStatic, "data", [])
    ) as StaticConfigItem[]
    const paidByInfo = paymentStatics?.[0]?.dict.find(
      (item) =>
        `${item.payment_method}` === `${currentPaymentMethod?.payment_method}`
    )

    const paidByIcon = paymentStatics?.[1]?.dict.find(
      (item) =>
        `${item.payment_method}` === `${currentPaymentMethod?.payment_method}`
    )

    return {
      info: paidByInfo?.value,
      icon: paidByIcon?.value,
    }
  }, [currentPaymentMethod, paymentStatic])

  const [snap, setSnap] = useState<number | string | null>(
    FIND_DRIVER_SNAP_POINTS[0]
  )

  const onClose = useCallback(() => {
    setSnap(FIND_DRIVER_SNAP_POINTS[0])
  }, [])

  return {
    pickup,
    dropoff,
    currentPaymentMethod,
    currentPaymentMethodStatic,
    countdownValue,
    snap,
    setSnap,
    snapPoints: FIND_DRIVER_SNAP_POINTS,
    onClose,
    sessionId,
    cancelledByName: beUser?.data?.user_name ?? "",
  }
}
